import { Injectable } from '@angular/core';
import { BehaviorSubject, concat, defer, EMPTY, fromEvent, Observable, of, throwError, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, mapTo, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { UserSessionService } from './usersession.service';

interface CacheEntry<T> {
  v: number;
  ts: number;
  data: T;
}

const KEY_PREFIX = 'dashboard_cache_';
// Bump when a response shape changes so old entries are ignored
const CACHE_VERSION = 1;
// Single entries above this (UTF-16 chars, ~2 MB) are never stored
const MAX_ENTRY_CHARS = 1000000;

/**
 * Stale-while-revalidate cache for dashboard API responses, persisted in localStorage.
 *
 * - swr(): emits the cached response first (if any), then the network response when the
 *   cache is missing or older than the TTL. Concurrent calls for the same key share one request.
 * - revalidate(): runs loaders with "network only" semantics (used by polling / refresh button).
 *   Responses that did not change are emitted as the cached object and flagged, so callers can
 *   skip a re-render with isUnchanged().
 * - poll(): ticks every period while the browser tab is visible.
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardCacheService {
  readonly ttlMs = 5 * 60 * 1000;

  private inFlight = new Map<string, Observable<any>>();
  private pendingCount = new BehaviorSubject<number>(0);
  private lastUpdated = new BehaviorSubject<number | null>(null);
  private staleKeys = new Set<string>();
  private staleSubject = new BehaviorSubject<boolean>(false);
  private unchanged = new WeakSet<object>();
  private forceNetwork = false;
  private lastRevalidateAt = Date.now();

  /** True while any dashboard request is on the wire */
  readonly refreshing$: Observable<boolean> = this.pendingCount.pipe(map(n => n > 0), distinctUntilChanged());
  /** Time (ms) of the most recent data shown: cache time for cached data, now for fresh data */
  readonly lastUpdated$: Observable<number | null> = this.lastUpdated.asObservable();
  /** True while some data on screen came from a cache entry older than the TTL */
  readonly stale$: Observable<boolean> = this.staleSubject.pipe(distinctUntilChanged());

  constructor(private userSessionService: UserSessionService) {}

  swr<T>(apiName: string, params: any, request: () => Observable<T>): Observable<T> {
    // Read synchronously so revalidate() only affects calls made inside its callback
    const networkOnly = this.forceNetwork;
    return defer(() => {
      const key = this.keyFor(apiName, params);
      const cached = this.read<T>(key);
      const isStale = !cached || Date.now() - cached.ts > this.ttlMs;

      if (networkOnly) {
        return this.fetch(key, request).pipe(
          map(fresh => {
            if (cached && this.sameData(fresh, cached.data)) {
              return this.markUnchanged(cached.data);
            }
            return fresh;
          }),
          // Keep the last good data on screen when the refresh fails
          catchError(err => cached ? of(this.markUnchanged(cached.data)) : throwError(err))
        );
      }

      const cached$ = cached
        ? of(cached.data).pipe(tap(() => {
            this.lastUpdated.next(cached.ts);
            if (isStale) this.setStale(key, true);
          }))
        : EMPTY;
      const fresh$ = isStale
        ? this.fetch(key, request).pipe(
            // Already showing identical cached data: nothing to re-render
            filter(fresh => !(cached && this.sameData(fresh, cached.data))),
            catchError(err => cached ? EMPTY : throwError(err))
          )
        : EMPTY;
      return concat(cached$, fresh$);
    });
  }

  /**
   * Runs `load` so every swr() call it makes synchronously goes to the network and skips the
   * cached emission. Completes once all dashboard requests have settled.
   */
  revalidate(load: () => void): Observable<void> {
    this.lastRevalidateAt = Date.now();
    this.forceNetwork = true;
    try {
      load();
    } finally {
      this.forceNetwork = false;
    }
    return this.pendingCount.pipe(filter(n => n === 0), take(1), mapTo(undefined));
  }

  /** True when `res` is a revalidated response identical to what was already cached */
  isUnchanged(res: any): boolean {
    return !!res && typeof res === 'object' && this.unchanged.has(res);
  }

  /** Ticks every `periodMs` while the tab is visible; on becoming visible, ticks at once if a refresh is overdue */
  poll(periodMs: number = this.ttlMs): Observable<void> {
    return fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => !document.hidden),
      distinctUntilChanged(),
      switchMap(visible => {
        if (!visible) return EMPTY;
        const due = Math.max(0, periodMs - (Date.now() - this.lastRevalidateAt));
        return timer(due, periodMs);
      }),
      mapTo(undefined)
    );
  }

  clear(): void {
    this.keys().forEach(k => this.remove(k));
  }

  private fetch<T>(key: string, request: () => Observable<T>): Observable<T> {
    let shared = this.inFlight.get(key) as Observable<T> | undefined;
    if (!shared) {
      this.pendingCount.next(this.pendingCount.value + 1);
      shared = request().pipe(
        tap(res => {
          if (this.isFailure(res)) {
            throw res;
          }
          this.write(key, res);
          this.setStale(key, false);
          this.lastUpdated.next(Date.now());
        }),
        finalize(() => {
          this.inFlight.delete(key);
          this.pendingCount.next(Math.max(0, this.pendingCount.value - 1));
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.inFlight.set(key, shared);
    }
    return shared;
  }

  private keyFor(apiName: string, params: any): string {
    const user = this.userSessionService.userId() || 0;
    return `${KEY_PREFIX}${apiName}_u${user}_${JSON.stringify(params || {})}`;
  }

  private read<T>(key: string): CacheEntry<T> | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const entry = JSON.parse(raw) as CacheEntry<T>;
      if (!entry || entry.v !== CACHE_VERSION || typeof entry.ts !== 'number') {
        this.remove(key);
        return null;
      }
      return entry;
    } catch {
      // Corrupt JSON or storage blocked
      this.remove(key);
      return null;
    }
  }

  private write<T>(key: string, data: T): void {
    let raw: string;
    try {
      raw = JSON.stringify({ v: CACHE_VERSION, ts: Date.now(), data } as CacheEntry<T>);
    } catch {
      return;
    }
    if (raw.length > MAX_ENTRY_CHARS) {
      this.remove(key);
      return;
    }
    try {
      localStorage.setItem(key, raw);
    } catch {
      // Quota exceeded: drop the oldest half of our entries and try once more
      this.evictOldest(0.5);
      try {
        localStorage.setItem(key, raw);
      } catch {
        this.remove(key);
      }
    }
  }

  private evictOldest(fraction: number): void {
    const entries = this.keys().map(k => {
      let ts = 0;
      try {
        ts = JSON.parse(localStorage.getItem(k) || '{}').ts || 0;
      } catch { }
      return { k, ts };
    }).sort((a, b) => a.ts - b.ts);
    entries.slice(0, Math.max(1, Math.ceil(entries.length * fraction))).forEach(e => this.remove(e.k));
  }

  private keys(): string[] {
    const out: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(KEY_PREFIX)) out.push(k);
      }
    } catch { }
    return out;
  }

  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch { }
  }

  private isFailure(res: any): boolean {
    return !res || res.isSuccess === false || res.IsSuccess === false;
  }

  private sameData(a: any, b: any): boolean {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }

  private markUnchanged<T>(data: T): T {
    if (data && typeof data === 'object') {
      this.unchanged.add(data as any);
    }
    return data;
  }

  private setStale(key: string, stale: boolean): void {
    if (stale) this.staleKeys.add(key); else this.staleKeys.delete(key);
    this.staleSubject.next(this.staleKeys.size > 0);
  }
}
